import type { Nullable } from 'corgi-types';
import { CartType } from 'corgi-types';
import type { AdditionalServiceType } from 'graphql-ts-types';

import type { Assembly } from '../components/ArticlePreview/types';
import { MessageType } from '../enums/Alert';
import {
  CartMessageCode,
  CartMessageText,
  CartVoucherAccountTypeMismatchMessageText,
} from '../enums/Cart';
import { CartEvent } from '../enums/Tracking';
import { executeQuery } from '../gql';
import articleLabelsFragment from '../gql/fragments/articleLabels.gql';
import cartAttributes from '../gql/fragments/cart.gql';
import shippingInfoAttributes from '../gql/fragments/shippingInfo.gql';
import shopAttributes from '../gql/fragments/shop.gql';
import type {
  AddToCartV2MutationVariables,
  AddToDigitalCartMutation,
  AddToDigitalCartMutationVariables,
} from '../gql/generated/schema';
import addToCartV2Query from '../gql/mutation/cart/add-to-cart-v2.gql';
import addToDigitalCartQuery from '../gql/mutation/cart/add-to-digital-cart.gql';
import cartResponseFragment from '../gql/mutation/cart/cart-response-fragment.gql';
import type { CartResponse } from '../gql/mutation/cart/types';
import { combineQueryWithFragments } from '../gql/utils/fragments';
import { getCartEventData } from '../tracking/cartEventData';
import { dispatchTrackingEventGA4 } from '../tracking/dispatchTrackingEventGA4';
import type { AlertMessage } from '../types/alert';
import type { ArticleTracking } from '../types/Article';
import type { CartMessage } from '../types/Cart';

interface TrackingEventOptions {
  tracking: ArticleTracking;
  quantity: number;
  cartGrandTotal: number;
  grandTotal: number;
  assembly: Nullable<Assembly>;
  tax: number;
  services: AdditionalServiceType[];
  couponCode: string;
  couponDiscountAmount?: Nullable<number>;
}

const WARNING_MESSAGE: AlertMessage = {
  colorType: MessageType.Warning,
  translation: 'h24_add_to_cart_warning',
};
const SUCCESS_MESSAGE: AlertMessage = {
  colorType: MessageType.Success,
  translation: 'h24_add_to_cart_success',
};

export const getMessageText = (code: Nullable<string>): CartMessageText => CartMessageText[code];

export const isVoucherNotHomeClubMemberErrorCode = (code: Nullable<string>) =>
  code === CartMessageCode.VoucherNotHomeClubMember;

export const isVoucherAccountTypeMismatchErrorCode = (code: Nullable<string>) =>
  code === CartMessageCode.VoucherAccountTypeMismatch;

const isWarningMessage = (code: CartMessageCode) => {
  switch (code) {
    case CartMessageCode.ItemQuantityAdjusted:
    case CartMessageCode.ItemMaxQuantityExceeded:
      return true;
    default:
      return false;
  }
};

const getAffectedItem = (sku: string, messages: CartMessage[]) =>
  messages.find(({ affectedItems }) =>
    affectedItems.find(({ article: { sku: affectedSku } }) => sku === affectedSku)
  );

export const getAlertMessage = (sku: string, messages: CartMessage[]): AlertMessage => {
  if (messages.length > 0) {
    const affectedItem = getAffectedItem(sku, messages);

    return affectedItem && isWarningMessage(affectedItem.code) ? WARNING_MESSAGE : SUCCESS_MESSAGE;
  }

  return SUCCESS_MESSAGE;
};

export const getVoucherAccountTypeMismatchMessageText = (isAnonymousUser?: boolean) =>
  isAnonymousUser
    ? CartVoucherAccountTypeMismatchMessageText.NotLoggedIn
    : CartVoucherAccountTypeMismatchMessageText.LoggedIn;

export const executeAddToCartV2 = (variables: AddToCartV2MutationVariables) =>
  executeQuery<CartResponse, AddToCartV2MutationVariables>({
    query: combineQueryWithFragments(
      addToCartV2Query,
      cartResponseFragment,
      cartAttributes,
      shopAttributes,
      shippingInfoAttributes,
      articleLabelsFragment
    ),
    variables,
  });

export const executeAddToDigitalCart = (variables: AddToDigitalCartMutationVariables) =>
  executeQuery<AddToDigitalCartMutation, AddToDigitalCartMutationVariables>({
    query: addToDigitalCartQuery,
    variables,
  });

export const dispatchAddToCartTrackingEvent = ({
  tracking,
  quantity,
  cartGrandTotal,
  grandTotal,
  tax,
  assembly,
  services,
  couponCode,
  couponDiscountAmount,
}: TrackingEventOptions) => {
  const changeValue = Math.abs(cartGrandTotal - grandTotal) / 100;
  const resultValue = grandTotal / 100;

  dispatchTrackingEventGA4(
    getCartEventData({
      event: CartEvent.AddToCart,
      changeValue,
      resultValue,
      tracking,
      quantity,
      assembly,
      tax,
      services,
      couponCode,
      couponDiscountAmount,
    })
  );
};

type ItemsGroups = Array<{ isHome24?: boolean }>;

const hasOnlyHome24Items = (itemsGroups: ItemsGroups) =>
  itemsGroups?.every(({ isHome24 }) => isHome24);
const hasMixedItems = (itemsGroups: ItemsGroups) => itemsGroups?.some(({ isHome24 }) => isHome24);

export const getCartType = ({ itemsGroups }: { itemsGroups: ItemsGroups }) => {
  switch (true) {
    case hasOnlyHome24Items(itemsGroups):
      return CartType.Home24;
    case hasMixedItems(itemsGroups):
      return CartType.Mixed;
    default:
      return CartType.Marketplace;
  }
};
